import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import { addRequest, getAllRequests, getSiteDoor } from "../../store/actions";
import {
  Button,
  Input,
  Select,
  DatePicker,
  Tabs,
  useToast,
  Breadcrumb,
  Card
} from "../UI";
import { getUserRole } from "../../utils/roles";
import { useDebounce } from "../../hooks/useDebounce";
import {
  PlusCircle,
  Save,
  Upload,
  Info,
  FileText,
  Inbox,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { axiosConfig } from "../../config/index";
import moment from "moment";
import { RequestValidationSchema } from "../../validations/index";
import { AlertCircle, Info as InfoIcon } from "lucide-react";

const AddRequest = ({ addRequest, user, getSiteDoor, siteDoor }: any) => {
  const history = useHistory();
  const { toast } = useToast();
  const [siteItems, setSiteItems] = useState<any[]>([]);
  const [doorItems, setDoorItems] = useState<any[]>([]);
  const [filteredDoorItems, setFilteredDoorItems] = useState<any[]>([]);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [searchText] = useState<string>("");
  const [, setEmployeeItems] = useState<any>([]);
  const [activeCard, setActiveCard] = useState<any>(null);
  const [activeCardLoading, setActiveCardLoading] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch standardized locations
  useEffect(() => {
    axios.get(`/api/sites/getalllocation/`, axiosConfig)
      .then(res => {
        setLocationOptions(res.data.map((l: any) => ({ label: l.location, value: l.location })));
      })
      .catch(err => console.error("Error fetching locations:", err));
  }, []);

  // Fetch the user's active card (for maintenance pre-population / new request blocking)
  useEffect(() => {
    if (!user?.oracleId) return;
    setActiveCardLoading(true);
    axios.get(`/api/activecards/employee/${user.oracleId}`, axiosConfig)
      .then(res => setActiveCard(res.data))
      .catch(() => setActiveCard(null))
      .finally(() => setActiveCardLoading(false));
  }, [user?.oracleId]);

  useEffect(() => {
    if (debouncedSearchText) {
      axios.get(`/api/requests/search/${debouncedSearchText}`, axiosConfig)
        .then((res: any) => {
          setEmployeeItems(res.data);
        })
        .catch(err => console.error("Search request error:", err));
    } else {
      setEmployeeItems([]);
    }
  }, [debouncedSearchText]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const sitesResponse = await axios.get(`/api/sites/getallsite/`, axiosConfig);
        const doorsResponse = await axios.get(`/api/doors/getalldoors/`, axiosConfig);
        setSiteItems(sitesResponse.data);
        setDoorItems(doorsResponse.data);
        getSiteDoor();
      } catch (err) {
        console.error("Load data error:", err);
      }
    };
    loadData();
  }, [getSiteDoor]);

  const formik = useFormik({
    initialValues: {
      requesttype: "new",
      requestername: user?.oracleId || "",
      requesterLocation: user?.location || "",
      allowedsite: [],
      alloweddoor: [],
      startdate: moment().format('YYYY-MM-DD'),
      requestDuration: "",
      reciept_attachment: null as File | null,
    },
    enableReinitialize: true,
    validationSchema: RequestValidationSchema,
    onSubmit: async (values, { setErrors, resetForm }) => {
      // Block 'new' if the employee already has an active card
      if (values.requesttype === "new" && activeCard) {
        const errorMsg = "You already have an active access card. Please use 'Maintenance' to update access, 'Replacement' for a lost/damaged card, or 'Deactivation' to remove it.";
        toast(errorMsg, "error");
        return;
      }
      if (values.requesttype !== "deactivation" && values.requestDuration && Number(values.requestDuration) > 180) {
        const errorMsg = "Request duration shouldn't exceed 180 days.";
        toast(errorMsg, "error");
        setErrors({ requestDuration: errorMsg } as any);
        return;
      }

      if (values.requesttype === "replacement" && !values.reciept_attachment) {
        const errorMsg = "Replacement requests need an attachment. Please upload an attachment.";
        toast(errorMsg, "error");
        setErrors({ reciept_attachment: errorMsg } as any);
        return;
      }

      try {
        await addRequest(values);
        toast("Request submitted successfully", "success");
        resetForm({});
        history.push("/requestlist");
      } catch (error: any) {
        console.error("Error submitting request:", error);
        setErrors(error);
        const errorMsg = error?.request || error?.message || "Failed to submit request";
        toast(typeof errorMsg === 'string' ? errorMsg : "Failed to submit request", "error");
      }
    },
  });

  const { setFieldValue, values, errors, touched, handleSubmit } = formik;

  // Filter sites based on selected location
  useEffect(() => {
    if (values.requesterLocation) {
      axios.get(`/api/sites/location/${values.requesterLocation}`, axiosConfig)
        .then(res => {
          setSiteItems(res.data);
          if (values.allowedsite.length > 0) {
            setFieldValue("allowedsite", []);
            setFieldValue("alloweddoor", []);
          }
        })
        .catch(err => console.error("Error fetching sites for location:", err));
    } else {
      setSiteItems([]);
      setFieldValue("allowedsite", [])
      setFieldValue("alloweddoor", []);
    }
  }, [values.requesterLocation, setFieldValue]);

  // Filter doors based on selected sites
  useEffect(() => {
    if (values.allowedsite.length > 0) {
      const filtered = doorItems.filter(door =>
        (values.allowedsite as any[]).includes(door.siteId)
      );
      setFilteredDoorItems(filtered);

      const validDoorIds = filtered.map(d => d.id);
      const updatedSelectedDoors = (values.alloweddoor as any[]).filter(id => validDoorIds.includes(id));
      if (updatedSelectedDoors.length !== values.alloweddoor.length) {
        setFieldValue("alloweddoor", updatedSelectedDoors);
      }
    } else {
      setFilteredDoorItems([]);
      if (values.alloweddoor.length > 0) {
        setFieldValue("alloweddoor", []);
      }
    }
  }, [values.allowedsite, doorItems, setFieldValue]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
      if (!isJpgOrPng) {
        toast("You can only upload JPG/PNG files!", "error");
        return;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        toast("Image must be smaller than 2MB!", "error");
        return;
      }
      setFieldValue("reciept_attachment", file);
    }
  };

  const formContent = (
    <div className="space-y-6 max-w-2xl min-h-[350px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-1">
        <div className="md:col-span-2">
          <Input
            label="Requester"
            value={user?.fullName || "Loading..."}
            disabled
            readOnly
          />
        </div>

        <Select
          label="Request Type"
          required
          options={[
            { label: "New", value: "new" },
            { label: "Replacement", value: "replacement" },
            { label: "Maintenance", value: "maintenance" },
            { label: "Deactivation", value: "deactivation" },
          ]}
          value={values.requesttype}
          onChange={(val) => {
            setFieldValue("requesttype", val);
            // Reset site/door selections for deactivation to avoid stale data
            if (val === "deactivation") {
              setFieldValue("allowedsite", []);
              setFieldValue("alloweddoor", []);
            }
            // Pre-populate doors & sites from active card for Maintenance
            if (val === "maintenance" && activeCard) {
              const siteIds = activeCard.allowedsite?.map((s: any) => s.id) || [];
              const doorIds = activeCard.alloweddoor?.map((d: any) => d.id) || [];
              setFieldValue("allowedsite", siteIds);
              setFieldValue("alloweddoor", doorIds);
            }
            // For Replacement: pre-fill with existing doors too, but leave editable
            if (val === "replacement" && activeCard) {
              const siteIds = activeCard.allowedsite?.map((s: any) => s.id) || [];
              const doorIds = activeCard.alloweddoor?.map((d: any) => d.id) || [];
              setFieldValue("allowedsite", siteIds);
              setFieldValue("alloweddoor", doorIds);
            }
          }}
          error={touched.requesttype ? errors.requesttype as string : ""}
        />

        <Select
          label="Region / Location"
          required
          options={locationOptions}
          value={values.requesterLocation}
          onChange={(val) => setFieldValue("requesterLocation", val)}
          error={touched.requesterLocation ? errors.requesterLocation as string : ""}
          placeholder="Select region..."
          searchable
        />

        {values.requesttype !== "deactivation" && (
          <>
            <Select
              label="Request Sites"
              multiple
              placeholder="Select sites..."
              options={siteItems.map(s => ({ label: s.sitename, value: s.id }))}
              value={values.allowedsite}
              onChange={(val) => setFieldValue("allowedsite", val)}
              error={touched.allowedsite ? errors.allowedsite as string : ""}
            />

            <Select
              label="Request Doors"
              multiple
              placeholder={values.allowedsite.length === 0 ? "Select sites first..." : "Select doors..."}
              options={filteredDoorItems.map(d => ({ label: d.alloweddoor, value: d.id }))}
              value={values.alloweddoor}
              onChange={(val) => setFieldValue("alloweddoor", val)}
              error={touched.alloweddoor ? errors.alloweddoor as string : ""}
            />

            <DatePicker
              label="Start Date"
              required
              value={values.startdate}
              onChange={(e) => setFieldValue("startdate", e.target.value)}
              error={touched.startdate ? errors.startdate as string : ""}
            />

            <Input
              label="Duration (Days)"
              placeholder="Enter number of days..."
              type="number"
              min="1"
              name="requestDuration"
              value={values.requestDuration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={touched.requestDuration ? errors.requestDuration as string : ""}
            />

            {values.requesttype === "replacement" && (
              <div className="md:col-span-2 space-y-2 pt-2">
                <label className="text-sm font-bold text-slate-700 px-1 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#8dc63f]" />
                  Receipt Attachment
                </label>
                <div className={`
                  relative h-32 rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center p-4
                  ${values.reciept_attachment ? 'border-[#8dc63f] bg-[#8dc63f]/10/30' : 'border-slate-200 hover:border-[#a3d853] bg-slate-50/50'}
                `}>
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                    accept=".jpeg,.png"
                  />
                  {values.reciept_attachment ? (
                    <div className="flex flex-col items-center gap-1 text-center">
                      <CheckCircle className="w-8 h-8 text-[#8dc63f]" />
                      <p className="text-sm font-bold text-slate-900 truncate max-w-[200px]">{(values.reciept_attachment as any).name}</p>
                      <button type="button" onClick={() => setFieldValue("reciept_attachment", "")} className="text-xs text-rose-500 font-bold hover:underline">Remove file</button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-slate-400">
                      <Inbox className="w-8 h-8 mb-1" />
                      <p className="text-sm font-bold">Click or drag to upload receipt</p>
                      <p className="text-xs font-medium">JPEG, PNG only (Max 2MB)</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-slate-100">
        <Button onClick={() => handleSubmit()} className="w-full sm:w-auto gap-2 shadow-lg shadow-[#8dc63f]/20">
          <Save className="w-4 h-4" />
          Submit Request
        </Button>
        <Link to="/requestlist" className="w-full sm:w-auto">
          <Button variant="ghost" className="w-full">Cancel</Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* 1. Page Header Section */}
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: getUserRole(user) === 'NORMAL_USER' ? '/requestlist' : '/dashboard' },
            { label: 'Requests', to: '/requestlist' },
            { label: 'Add New' }
          ]}
        />
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#8dc63f]/15 rounded-xl flex items-center justify-center">
            <PlusCircle className="w-4 h-4 text-[#8dc63f]" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Create Access Request</h1>
        </div>
      </div>

      {/* 2. Main Two-Column Panel Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

        {/* LEFT COLUMN: Input form elements container area (Spans 2/3 width) */}
        <div className="lg:col-span-2 space-y-6 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-1">
            <div className="md:col-span-2">
              <Input
                label="Requester"
                value={user?.fullName || "Loading..."}
                disabled
                readOnly
              />
            </div>

            <Select
              label="Request Type"
              required
              options={[
                { label: "New", value: "new" },
                { label: "Replacement", value: "replacement" },
                { label: "Maintenance", value: "maintenance" },
                { label: "Deactivation", value: "deactivation" },
              ]}
              value={values.requesttype}
              onChange={(val) => {
                setFieldValue("requesttype", val);
                // Reset site/door selections for deactivation to avoid stale data
                if (val === "deactivation") {
                  setFieldValue("allowedsite", []);
                  setFieldValue("alloweddoor", []);
                }
                if (val === "maintenance" && activeCard) {
                  const siteIds = activeCard.allowedsite?.map((s: any) => s.id) || [];
                  const doorIds = activeCard.alloweddoor?.map((d: any) => d.id) || [];
                  setFieldValue("allowedsite", siteIds);
                  setFieldValue("alloweddoor", doorIds);
                }
                if (val === "replacement" && activeCard) {
                  const siteIds = activeCard.allowedsite?.map((s: any) => s.id) || [];
                  const doorIds = activeCard.alloweddoor?.map((d: any) => d.id) || [];
                  setFieldValue("allowedsite", siteIds);
                  setFieldValue("alloweddoor", doorIds);
                }
              }}
              error={touched.requesttype ? errors.requesttype as string : ""}
            />

            <Select
              label="Region / Location"
              required
              options={locationOptions}
              value={values.requesterLocation}
              onChange={(val) => setFieldValue("requesterLocation", val)}
              error={touched.requesterLocation ? errors.requesterLocation as string : ""}
              placeholder="Select region..."
              searchable
            />

            {values.requesttype !== "deactivation" && (
              <>
                <Select
                  label="Request Sites"
                  multiple
                  placeholder="Select sites..."
                  options={siteItems.map(s => ({ label: s.sitename, value: s.id }))}
                  value={values.allowedsite}
                  onChange={(val) => setFieldValue("allowedsite", val)}
                  error={touched.allowedsite ? errors.allowedsite as string : ""}
                />

                <Select
                  label="Request Doors"
                  multiple
                  placeholder={values.allowedsite.length === 0 ? "Select sites first..." : "Select doors..."}
                  options={filteredDoorItems.map(d => ({ label: d.alloweddoor, value: d.id }))}
                  value={values.alloweddoor}
                  onChange={(val) => setFieldValue("alloweddoor", val)}
                  error={touched.alloweddoor ? errors.alloweddoor as string : ""}
                />

                <DatePicker
                  label="Start Date"
                  required
                  value={values.startdate}
                  onChange={(e) => setFieldValue("startdate", e.target.value)}
                  error={touched.startdate ? errors.startdate as string : ""}
                />

                <Input
                  label="Duration (Days)"
                  placeholder="Enter number of days..."
                  type="number"
                  min="1"
                  name="requestDuration"
                  value={values.requestDuration}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={touched.requestDuration ? errors.requestDuration as string : ""}
                />
              </>
            )}
          </div>

          {/* Form Trigger Actions Section */}
          <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-slate-50">
            <Button onClick={() => handleSubmit()} className="w-full sm:w-auto gap-2 shadow-lg shadow-[#8dc63f]/20">
              <Save className="w-4 h-4" />
              Submit Request
            </Button>
            <Link to="/requestlist" className="w-full sm:w-auto">
              <Button variant="ghost" className="w-full">Cancel</Button>
            </Link>
          </div>
        </div>

        {/* RIGHT COLUMN: Secondary Contextual Sidebar Panel (Spans 1/3 width) */}
        <div className="space-y-4">

          {/* A. The File Upload Box Container (Only renders for replacements) */}
          {values.requesttype === "replacement" && (
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm space-y-3">
              <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Upload className="w-4 h-4 text-[#8dc63f]" />
                Receipt Attachment
              </label>
              <div className={`
                relative h-32 rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center p-4 text-center
                ${values.reciept_attachment ? 'border-[#8dc63f] bg-[#8dc63f]/5' : 'border-slate-200 hover:border-[#a3d853] bg-slate-50/50'}
              `}>
                {!values.reciept_attachment && (
                  <input
                    type="file"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                    accept=".jpeg,.png"
                  />
                )}
                {values.reciept_attachment ? (
                  <div className="flex flex-col items-center gap-1">
                    <CheckCircle className="w-7 h-7 text-[#8dc63f]" />
                    <p className="text-xs font-bold text-slate-900 truncate max-w-[180px]">
                      {(values.reciept_attachment as any).name}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = URL.createObjectURL(values.reciept_attachment as File);
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        className="text-[11px] text-[#8dc63f] font-bold hover:underline flex items-center gap-0.5"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={() => setFieldValue("reciept_attachment", null)}
                        className="text-[11px] text-rose-500 font-bold hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-slate-400">
                    <Inbox className="w-7 h-7 mb-1 text-slate-300" />
                    <p className="text-xs font-bold text-slate-700">Click or drag file here</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">JPEG, PNG up to 2MB</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* B. Notice Banners Block Element Stack */}
          {values.requesttype === "new" && activeCard && (
            <div className="bg-amber-50/50 border border-amber-200/60 p-4 rounded-xl flex gap-3.5">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <h4 className="text-xs font-bold text-amber-950">
                  Active Card Detected (<span className="font-mono">{activeCard.physicalCardId}</span>)
                </h4>
                <p className="text-xs text-amber-900/90 font-normal leading-relaxed">
                  You cannot submit a new request while a card is active. Please select <strong className="font-semibold text-amber-950">Maintenance</strong> to update access, <strong className="font-semibold text-amber-950">Replacement</strong> for lost cards, or <strong className="font-semibold text-amber-950">Deactivation</strong> to cancel it.
                </p>
              </div>
            </div>
          )}

          {values.requesttype === "maintenance" && activeCard && (
            <div className="bg-[#8dc63f]/5 border border-[#8dc63f]/30 p-4 rounded-xl flex gap-3.5">
              <InfoIcon className="w-5 h-5 text-[#8dc63f] shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <h4 className="text-xs font-bold text-slate-800">
                  Pre-filled from Active Card (<span className="font-mono text-[#8dc63f] font-semibold">#{activeCard.physicalCardId}</span>)
                </h4>
                <p className="text-xs text-slate-600 font-normal leading-relaxed">
                  Sites and doors have been automatically populated from your active card. Modify your access permissions below as needed before submitting.
                </p>
              </div>
            </div>
          )}

          {values.requesttype === "replacement" && activeCard && (
            <div className="bg-slate-50 border border-slate-200/80 p-4 rounded-2xl flex gap-3">
              <Info className="w-5 h-5 text-[#8dc63f] shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-slate-800">
                  Card Replacement Policy (<span className="font-mono text-slate-900 font-bold">#{activeCard?.physicalCardId}</span>)
                </h4>
                <p className="text-xs text-slate-600 font-normal leading-relaxed">
                  Your active card will be deactivated upon issuance. You can modify your access permissions as needed. A <strong className="font-semibold text-slate-800">receipt attachment</strong> is required.
                </p>

                <div className="pt-1">
                  <a
                    href="../../assets/Maintenance_form.xlsx"
                    download
                    className="text-xs text-[#8dc63f] font-semibold hover:text-[#7cb037] hover:underline transition-colors"
                  >
                    Download Replacement Template
                  </a>
                </div>
              </div>
            </div>
          )}

          {values.requesttype === "replacement" && !activeCard && !activeCardLoading && (
            <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl flex gap-3.5">
              <Info className="w-5 h-5 text-[#8dc63f] shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Notice</h4>
                <p className="text-xs md:text-sm text-slate-600 font-normal leading-relaxed">
                  Replacement requests require a template upload.
                </p>
                <div className="pt-1">
                  <a href="../../assets/Maintainance_form.xlsx" download className="text-xs text-[#8dc63f] font-bold underline hover:text-[#7cb037] transition-all">
                    Download replacement template here
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
  siteDoor: state.site.siteDoor,
});

const mapDispatchToProps = {
  getAllRequests,
  addRequest,
  getSiteDoor
};

export default connect(mapStateToProps, mapDispatchToProps)(AddRequest);



